import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Injectable, PLATFORM_ID, inject, signal } from '@angular/core';
import { catchError, of, tap } from 'rxjs';
import { environment } from '../../../environments/environment';

export type WorkspaceBranding = {
  orgId?: string | null;
  orgName?: string | null;
  logoUrl?: string | null;
  coverImageUrl?: string | null;
  primaryColor?: string | null;
  accentColor?: string | null;
  portalSlug?: string | null;
  primaryDomain?: string | null;
  currency?: string | null;
  locale?: string | null;
  dateFormat?: string | null;
  timeZone?: string | null;
};

const TENANT_BRANDING_STORAGE_KEY = 'apexTenantWorkspaceBranding.v2';
const TENANT_FALLBACK_PRIMARY = '#64748B';
const TENANT_FALLBACK_ACCENT = '#94A3B8';

@Injectable({ providedIn: 'root' })
export class WorkspaceBrandingService {
  private readonly http = inject(HttpClient);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly base = `${environment.apiGatewayUrl}/property-service`;

  readonly branding = signal<WorkspaceBranding>({
    orgName: 'Tenant Portal',
    primaryColor: TENANT_FALLBACK_PRIMARY,
    accentColor: TENANT_FALLBACK_ACCENT,
  });

  apply(branding: WorkspaceBranding | null | undefined): void {
    if (!branding) {
      return;
    }

    const primaryColor = this.normalizeColor(branding.primaryColor, TENANT_FALLBACK_PRIMARY);
    const accentColor = this.normalizeColor(branding.accentColor, primaryColor || TENANT_FALLBACK_ACCENT);
    const next = {
      ...this.branding(),
      ...branding,
      dateFormat: branding.dateFormat ?? null,
      timeZone: branding.timeZone ?? null,
      orgName: branding.orgName?.trim() || this.branding().orgName || 'Tenant Portal',
      primaryColor,
      accentColor,
    };
    this.branding.set(next);
    this.applyColors(next.primaryColor, next.accentColor);
    this.rememberBranding(next);
  }

  load(workspace: string | null): void {
    const key = workspace?.trim() || this.workspaceFromBrowser();
    this.applyCachedBranding(key);
    if (!key) {
      this.applyColors(this.branding().primaryColor, this.branding().accentColor);
      return;
    }

    if (this.isUuid(key)) {
      this.applyColors(this.branding().primaryColor, this.branding().accentColor);
      return;
    }

    this.http
      .get<WorkspaceBranding>(`${this.base}/api/v1/public/workspaces/${encodeURIComponent(key)}/branding`, {
        withCredentials: true,
      })
      .pipe(
        tap((branding) => this.apply(branding)),
        catchError(() => {
          this.applyColors(this.branding().primaryColor, this.branding().accentColor);
          return of(null);
        }),
      )
      .subscribe();
  }

  private workspaceFromBrowser(): string | null {
    if (!isPlatformBrowser(this.platformId)) {
      return null;
    }

    const url = new URL(window.location.href);
    const queryWorkspace = url.searchParams.get('workspace') || url.searchParams.get('org');
    if (queryWorkspace?.trim()) {
      return queryWorkspace.trim();
    }

    const queryOrgId = url.searchParams.get('orgId');
    if (queryOrgId?.trim()) {
      return queryOrgId.trim();
    }

    const host = url.hostname.toLowerCase();
    if (!host || host.includes('localhost')) {
      return null;
    }

    return this.workspaceFromHost(host);
  }

  private workspaceFromHost(host: string): string | null {
    const portalHosts = [
      'tenant-dev.apex-habitat.com',
      'tenant.apex-habitat.com',
      'backoffice-dev.apex-habitat.com',
      'backoffice.apex-habitat.com',
      'owner-dev.apex-habitat.com',
      'owner.apex-habitat.com',
      'platform-admin-dev.apex-habitat.com',
      'platform-admin.apex-habitat.com',
      'admin-dev.apex-habitat.com',
      'admin.apex-habitat.com',
    ];

    for (const portalHost of portalHosts) {
      if (host === portalHost) {
        return null;
      }

      if (host.endsWith(`.${portalHost}`)) {
        return host.slice(0, -(portalHost.length + 1)).split('.')[0] || null;
      }
    }

    return host;
  }

  private applyColors(primary?: string | null, accent?: string | null): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const primaryColor = this.normalizeColor(primary, TENANT_FALLBACK_PRIMARY);
    const accentColor = this.normalizeColor(accent, primaryColor);
    const targets = [document.documentElement];
    if (document.body) {
      targets.push(document.body);
    }
    targets.forEach((target) => {
      target.style.setProperty('--workspace-primary', primaryColor);
      target.style.setProperty('--workspace-accent', accentColor);
      target.style.setProperty('--workspace-secondary', accentColor);
      target.style.setProperty('--tenant-primary', primaryColor);
      target.style.setProperty('--tenant-accent', accentColor);
      target.style.setProperty('--primary-color', primaryColor);
      target.style.setProperty('--primary', primaryColor);
      target.style.setProperty('--p-primary-color', primaryColor);
      target.style.setProperty('--p-primary-hover-color', this.mixColor(primaryColor, '#000000', 0.88));
      target.style.setProperty('--p-primary-active-color', this.mixColor(primaryColor, '#000000', 0.78));
      target.style.setProperty('--p-focus-ring-color', this.withAlpha(primaryColor, 0.22));
    });
  }

  private applyCachedBranding(workspace: string | null): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    try {
      const raw = window.localStorage.getItem(TENANT_BRANDING_STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      const cacheKey = workspace?.trim().toLowerCase() || parsed?.lastWorkspace || parsed?.lastOrgId;
      const cached = cacheKey ? parsed?.byWorkspace?.[cacheKey] || parsed?.byOrg?.[cacheKey] : null;
      const cachedPrimary = this.normalizeColor(cached?.primaryColor, '');
      if (!cachedPrimary) {
        return;
      }
      const cachedAccent = this.normalizeColor(cached?.accentColor, cachedPrimary);

      this.branding.set({
        ...this.branding(),
        ...cached,
        primaryColor: cachedPrimary,
        accentColor: cachedAccent,
      });
      this.applyColors(cachedPrimary, cachedAccent);
    } catch {
      // Cached branding only prevents refresh flicker; live branding still works without storage.
    }
  }

  private rememberBranding(branding: WorkspaceBranding): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const primaryColor = this.normalizeColor(branding.primaryColor, '');
    if (!primaryColor) {
      return;
    }

    const accentColor = this.normalizeColor(branding.accentColor, primaryColor);
    const workspace = (branding.portalSlug || this.workspaceFromBrowser() || '').trim().toLowerCase();
    const orgId = branding.orgId?.trim();

    try {
      const raw = window.localStorage.getItem(TENANT_BRANDING_STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : {};
      const byWorkspace = parsed && typeof parsed === 'object' && parsed.byWorkspace && typeof parsed.byWorkspace === 'object'
        ? parsed.byWorkspace
        : {};
      const byOrg = parsed && typeof parsed === 'object' && parsed.byOrg && typeof parsed.byOrg === 'object'
        ? parsed.byOrg
        : {};
      const cached = {
        orgId,
        orgName: branding.orgName,
        logoUrl: branding.logoUrl,
        primaryColor,
        accentColor,
        currency: branding.currency,
        locale: branding.locale,
      };

      window.localStorage.setItem(TENANT_BRANDING_STORAGE_KEY, JSON.stringify({
        lastWorkspace: workspace || parsed?.lastWorkspace || null,
        lastOrgId: orgId || parsed?.lastOrgId || null,
        byWorkspace: workspace ? { ...byWorkspace, [workspace]: cached } : byWorkspace,
        byOrg: orgId ? { ...byOrg, [orgId]: cached } : byOrg,
      }));
    } catch {
      // Cached branding only prevents refresh flicker; live branding still works without storage.
    }
  }

  private normalizeColor(value: string | null | undefined, fallback: string): string {
    const color = value?.trim();
    if (!color || !/^#[0-9A-Fa-f]{6}$/.test(color)) {
      return fallback;
    }

    return color.toUpperCase();
  }

  private mixColor(color: string, target: string, weight: number): string {
    const sourceRgb = this.hexToRgb(color);
    const targetRgb = this.hexToRgb(target);
    if (!sourceRgb || !targetRgb) {
      return color;
    }
    const mix = sourceRgb.map((channel, index) => Math.round(channel * weight + targetRgb[index] * (1 - weight)));
    return this.rgbToHex(mix);
  }

  private withAlpha(color: string, alpha: number): string {
    const rgb = this.hexToRgb(color);
    return rgb ? `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${alpha})` : color;
  }

  private hexToRgb(color: string): [number, number, number] | null {
    const match = /^#([0-9A-Fa-f]{6})$/.exec(color.trim());
    if (!match) {
      return null;
    }
    const value = match[1];
    return [
      Number.parseInt(value.slice(0, 2), 16),
      Number.parseInt(value.slice(2, 4), 16),
      Number.parseInt(value.slice(4, 6), 16),
    ];
  }

  private rgbToHex(rgb: number[]): string {
    return `#${rgb.map((channel) => Math.max(0, Math.min(255, channel)).toString(16).padStart(2, '0')).join('').toUpperCase()}`;
  }

  private isUuid(value: string): boolean {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value.trim());
  }
}
